<template>
  <div class="min-h-screen bg-white">
    <Header />

    <main>
      <!-- ═══ HERO — bright, matches landing aesthetic ═══ -->
      <section class="relative overflow-hidden bg-white pt-24 pb-8 md:pt-28 md:pb-10">
        <!-- Paw pattern overlay -->
        <div class="paw-pattern pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true" />

        <div class="relative mx-auto max-w-6xl px-4 md:px-6">
          <div class="mx-auto max-w-2xl text-center">
            <h1 class="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-6xl">
              {{ dynamicHeading }}
            </h1>

            <!-- Search pill — same vocabulary as landing hero -->
            <!-- ServicePicker has no pre-fill API: shows "What service?" on load even when
                 ?type=grooming is set. The filter bar shows the active state with brand color. -->
            <div class="mx-auto mt-7 max-w-xl">
              <form class="flex flex-col overflow-hidden rounded-full border border-black/10 bg-white shadow-xl sm:flex-row sm:items-stretch" @submit.prevent>
                <ServicePicker
                  class="sm:flex-1"
                  @select="onHeroServiceSelect"
                  @clear="onHeroServiceClear"
                />
                <div class="sm:flex-1 sm:min-h-[4rem] sm:border-l sm:border-black/8">
                  <CityPicker
                    v-model="locationQuery"
                    @select="onHeroCitySelect"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ STICKY FILTER BAR ═══ -->
      <ClientOnly>
        <ServiceFilterBar
          :model-service-type="activeServiceType"
          :model-city="activeCity"
          :model-sort="activeSort"
          :model-radius="searchRadius"
          :model-min-rating="activeMinRating"
          :model-min-price="activeMinPrice"
          :model-max-price="activeMaxPrice"
          :cities="cities"
          :result-count="servicesList.length"
          :is-locating="isLocating"
          :has-location="hasLocation"
          :view="viewMode"
          @update:model-service-type="handleServiceTypeChange"
          @update:model-city="handleCityChange"
          @update:model-sort="handleSortChange"
          @update:model-radius="handleRadiusChange"
          @update:model-min-rating="v => activeMinRating = v"
          @update:model-min-price="v => activeMinPrice = v"
          @update:model-max-price="v => activeMaxPrice = v"
          @update:view="v => viewMode = v"
          @request-location="handleRequestLocation"
        />
      </ClientOnly>

      <!-- ═══ MAP VIEW ═══ -->
      <div v-if="viewMode === 'map'" class="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div class="h-125 md:h-150 overflow-hidden rounded-3xl border border-border/50">
          <SearchResultsMap :services="servicesList" />
        </div>
      </div>

      <!-- ═══ RESULTS ═══ -->
      <section v-if="viewMode === 'list'" class="py-8">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <Skeleton name="website-services-search" :loading="status === 'pending'">
            <!-- Error State -->
            <div v-if="error" class="flex flex-col items-center justify-center py-20">
              <div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <Icon name="tabler:alert-circle" class="text-destructive" size="40" />
              </div>
              <p class="text-lg font-medium text-foreground">Something went wrong</p>
              <p class="mt-1 text-muted-foreground">{{ error.message }}</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="servicesList.length === 0" class="flex flex-col items-center justify-center py-20">
              <div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                <Icon name="tabler:search-off" class="text-muted-foreground" size="40" />
              </div>
              <h3 class="mb-2 text-xl font-semibold text-foreground">{{ $ts('servicesSearch.noResults.title') }}</h3>
              <p class="max-w-md text-center text-muted-foreground">{{ $ts('servicesSearch.noResults.description') }}</p>
              <button
                v-if="activeServiceType || activeCity"
                class="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-80"
                @click="clearFilters"
              >
                <Icon name="tabler:filter-off" size="16" />
                {{ $ts('servicesSearch.noResults.clearFilters') }}
              </button>
            </div>

            <div v-else>
              <div
                ref="virtualListRef"
                :style="{ height: `${virtualTotalSize}px`, position: 'relative' }"
              >
                <div
                  v-for="virtualRow in virtualItems"
                  :key="virtualRow.key as PropertyKey"
                  :ref="(el) => measureVirtualRow(el as HTMLElement | null)"
                  :data-index="virtualRow.index"
                  :style="{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start - listOffsetTop}px)`,
                  }"
                >
                  <div class="grid gap-5 pb-5 sm:grid-cols-2 lg:grid-cols-3">
                    <ServiceSearchCard
                      v-for="service in gridRows[virtualRow.index]"
                      :key="service._id"
                      :service="service"
                    />
                  </div>
                </div>
              </div>

              <div ref="sentinelRef" class="flex h-16 items-center justify-center">
                <Icon v-if="isLoadingMore" name="tabler:loader-2" size="20" class="animate-spin text-muted-foreground" />
              </div>

              <p v-if="!nextCursor && servicesList.length > 0" class="mt-2 text-center text-sm text-muted-foreground">
                {{ $ts('servicesSearch.pagination.showing').replace('{shown}', String(servicesList.length)).replace('{total}', String(totalEstimate ?? servicesList.length)) }}
              </p>
            </div>

            <template #fallback>
              <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div
                  v-for="i in 9"
                  :key="i"
                  class="aspect-[4/5] animate-pulse rounded-3xl bg-muted"
                />
              </div>
            </template>
          </Skeleton>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineAsyncComponent, onMounted, onBeforeUnmount } from 'vue';
import { useWindowVirtualizer } from '@tanstack/vue-virtual';
import Skeleton from 'boneyard-js/vue';
import { useRoute, useRouter, useAsyncData, useSeoMeta, useHead, useI18n, definePageMeta, useRuntimeConfig, useSchemaOrg, defineWebPage, useState } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import ServiceSearchCard from '#components/pages/services/ServiceSearchCard.vue';
import ServiceFilterBar from '#components/pages/services/ServiceFilterBar.vue';
import ServicePicker from '#components/ServicePicker.vue';
import CityPicker from '#components/CityPicker.vue';
import { usePostHog } from '#composables/usePostHog';
import { CITY_CENTERS, DEFAULT_RADIUS_KM } from '#bubba/shared/utils/geo';
import type { SearchServiceItem } from '#bubba/shared/types/search';
import type { ServiceType } from '#components/ServicePicker.vue';
import type { GeocodeResult } from '#types/geo';

// Lazy-load the map component (heavy MapLibre dependency)
const SearchResultsMap = defineAsyncComponent(
  () => import('#components/pages/services/SearchResultsMap.vue'),
);

const { $ts, $localeRoute } = useI18n();
const { captureEvent } = usePostHog();

definePageMeta({
  layout: false,
});

// Signal the header to switch to full-width search-page mode
const headerSearchPage = useState<boolean>('headerSearchPage', () => false);
onMounted(() => { headerSearchPage.value = true; });
onBeforeUnmount(() => { headerSearchPage.value = false; });

useSchemaOrg([
  defineWebPage({ '@type': 'SearchResultsPage' }),
]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Services' },
]);

const route = useRoute();
const router = useRouter();

// Read initial values from query params
const searchQuery = ref<string>((route.query.q as string) || '');
const locationQuery = ref<string>((route.query.city as string) || '');
const activeSearchQuery = ref<string | undefined>(
  (route.query.q as string) || undefined
);
const activeServiceType = ref<string | undefined>(
  (route.query.type as string) || undefined
);
const activeCity = ref<string | undefined>(
  (route.query.city as string) || undefined
);
const activeSort = ref<string>(
  (route.query.sort as string) || 'popular'
);

const viewMode = ref<'list' | 'map'>('list');

// Location state
const userLat = ref<number | undefined>(undefined);
const userLng = ref<number | undefined>(undefined);
const searchRadius = ref(DEFAULT_RADIUS_KM);
const isLocating = ref(false);
const hasLocation = computed(() => userLat.value != null && userLng.value != null);

// Additional filters
const activeMinRating = ref<number | undefined>(undefined);
const activeMinPrice = ref<number | undefined>(undefined);
const activeMaxPrice = ref<number | undefined>(undefined);

function serviceTypeLabel(type: string): string {
  return $ts(`servicesSearch.serviceTypes.${type}`);
}

// Dynamic heading: "Grooming in Vilnius" / "Pet Services" / "Grooming" / "Services in Vilnius"
const dynamicHeading = computed(() => {
  const typeName = activeServiceType.value ? serviceTypeLabel(activeServiceType.value) : null;
  const city = activeCity.value;
  if (typeName && city) return `${typeName} in ${city}`;
  if (typeName) return typeName;
  if (city) return `${$ts('servicesSearch.title')} in ${city}`;
  return $ts('servicesSearch.title');
});

// Pagination sizes: first page shows 9 cards (a 3×3 grid), then 6 per scroll fetch.
const INITIAL_PAGE_SIZE = 9;
const NEXT_PAGE_SIZE = 6;

// Build query params for the API request
const fetchParams = computed(() => {
  const params: Record<string, string> = { limit: String(INITIAL_PAGE_SIZE) };
  if (activeSearchQuery.value) params.q = activeSearchQuery.value;
  if (activeServiceType.value) params.type = activeServiceType.value;
  if (activeCity.value) params.city = activeCity.value;
  if (activeSort.value && activeSort.value !== 'popular') params.sort = activeSort.value;
  if (userLat.value != null) params.lat = String(userLat.value);
  if (userLng.value != null) params.lng = String(userLng.value);
  if (userLat.value != null || activeCity.value) params.radius = String(searchRadius.value);
  if (activeMinRating.value != null) params.minRating = String(activeMinRating.value);
  if (activeMinPrice.value != null) params.minPrice = String(activeMinPrice.value);
  if (activeMaxPrice.value != null) params.maxPrice = String(activeMaxPrice.value);
  return params;
});

interface SearchResponse {
  results: SearchServiceItem[];
  nextCursor: string | null;
  totalEstimate: number | null;
}

const allResults = ref<SearchServiceItem[]>([]);
const nextCursor = ref<string | null>(null);
const totalEstimate = ref<number | null>(null);
const isLoadingMore = ref(false);
const searchVersion = ref(0);

// Server-side filtered fetch
const { data: searchResponse, error, status } = await useAsyncData(
  'services-search',
  () => $fetch<SearchResponse>('/api/services', { params: fetchParams.value }),
  { watch: [fetchParams] },
);

// Reset and populate on initial/filter-change fetch
watch(searchResponse, (response) => {
  if (response) {
    searchVersion.value++;
    allResults.value = response.results ?? [];
    nextCursor.value = response.nextCursor ?? null;
    totalEstimate.value = response.totalEstimate ?? null;

    // Analytics
    if (import.meta.client) {
      captureEvent('search_performed', {
        query: activeSearchQuery.value || null,
        serviceType: activeServiceType.value || null,
        city: activeCity.value || null,
        sortBy: activeSort.value || null,
        resultCount: response.results?.length ?? 0,
      });
      if ((response.results?.length ?? 0) === 0) {
        captureEvent('search_no_results', {
          query: activeSearchQuery.value || null,
          serviceType: activeServiceType.value || null,
          city: activeCity.value || null,
        });
      }
    }
  }
}, { immediate: true });

const servicesList = computed(() => allResults.value);

const COLS_PER_ROW = 3;

const gridRows = computed<SearchServiceItem[][]>(() => {
  const rows: SearchServiceItem[][] = [];
  for (let i = 0; i < servicesList.value.length; i += COLS_PER_ROW) {
    rows.push(servicesList.value.slice(i, i + COLS_PER_ROW));
  }
  return rows;
});

const virtualListRef = ref<HTMLElement | null>(null);
const listOffsetTop = ref(0);
let listOffsetObserver: ResizeObserver | null = null;

function updateListOffset() {
  if (!import.meta.client || !virtualListRef.value) return;
  listOffsetTop.value =
    virtualListRef.value.getBoundingClientRect().top + window.scrollY;
}

const rowVirtualizer = useWindowVirtualizer(
  computed(() => ({
    count: gridRows.value.length,
    estimateSize: () => 500,
    overscan: 2,
    scrollMargin: listOffsetTop.value,
  })),
);

// `virtualRow.start` is in document coordinates (includes scrollMargin), but
// rows are positioned inside a wrapper that already sits scrollMargin pixels
// down the page — so we subtract scrollMargin when translating. `getTotalSize()`
// already subtracts scrollMargin internally, so we use it raw.
const virtualItems = computed(() => rowVirtualizer.value.getVirtualItems());
const virtualTotalSize = computed(() => rowVirtualizer.value.getTotalSize());

function measureVirtualRow(el: HTMLElement | null) {
  if (!el) return;
  rowVirtualizer.value.measureElement(el);
}

onMounted(() => {
  updateListOffset();
  window.addEventListener('resize', updateListOffset);
  // Upstream layout (hero, filter bar, async fonts) can shift the wrapper
  // after mount — re-read the offset when the document body resizes.
  if (typeof ResizeObserver !== 'undefined') {
    listOffsetObserver = new ResizeObserver(updateListOffset);
    listOffsetObserver.observe(document.body);
  }
});
onBeforeUnmount(() => {
  if (!import.meta.client) return;
  window.removeEventListener('resize', updateListOffset);
  listOffsetObserver?.disconnect();
});

// After firing, unobserve until the new page lands — otherwise the sentinel
// remains within the 200px rootMargin and triggers a burst of requests.
const sentinelRef = ref<HTMLElement | null>(null);
let sentinelObserver: IntersectionObserver | null = null;

function setupSentinel() {
  if (!import.meta.client || !sentinelRef.value) return;
  sentinelObserver?.disconnect();
  sentinelObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      if (!nextCursor.value || isLoadingMore.value) return;
      sentinelObserver?.unobserve(sentinelRef.value!);
      loadMore().finally(() => {
        if (sentinelRef.value && nextCursor.value) {
          sentinelObserver?.observe(sentinelRef.value);
        }
      });
    },
    { rootMargin: '200px' },
  );
  sentinelObserver.observe(sentinelRef.value);
}

watch(sentinelRef, (el) => { if (el) setupSentinel(); });
onBeforeUnmount(() => { sentinelObserver?.disconnect(); });

watch(viewMode, (v) => {
  captureEvent('search_map_toggled', { view: v });
});

async function loadMore() {
  if (!nextCursor.value || isLoadingMore.value) return;
  const currentVersion = searchVersion.value;
  isLoadingMore.value = true;
  try {
    const params = {
      ...fetchParams.value,
      limit: String(NEXT_PAGE_SIZE),
      cursor: nextCursor.value,
    };
    const response = await $fetch<SearchResponse>('/api/services', { params });

    // Filters changed mid-flight — discard this page, the new search owns the list.
    if (currentVersion !== searchVersion.value) return;

    allResults.value = [...allResults.value, ...response.results];
    nextCursor.value = response.nextCursor;
    captureEvent('search_page_loaded', {
      page: Math.ceil(allResults.value.length / NEXT_PAGE_SIZE),
      resultCount: allResults.value.length,
    });
  } finally {
    isLoadingMore.value = false;
  }
}

// Fetch available cities from dedicated API route
const { data: citiesData } = await useAsyncData(
  'services-cities',
  () => $fetch('/api/cities'),
);

const cities = computed(() => {
  if (!citiesData.value) return [];
  return citiesData.value.map((c: { name: string; count: number }) => c.name);
});

// SEO meta with reactive getters
useSeoMeta({
  title: () => {
    const parts = [$ts('servicesSearch.title')];
    if (activeServiceType.value) parts.push(serviceTypeLabel(activeServiceType.value));
    if (activeCity.value) parts.push(activeCity.value);
    return parts.join(' - ');
  },
  description: () => $ts('servicesSearch.subtitle'),
});

// Canonical URL + JSON-LD structured data
const runtimeConfig = useRuntimeConfig();
const siteUrl = runtimeConfig.public.siteUrl;

useHead({
  link: [
    {
      rel: 'canonical',
      href: () => {
        const base = `${siteUrl}/services`;
        const params = new URLSearchParams();
        if (activeServiceType.value) params.set('type', activeServiceType.value);
        if (activeCity.value) params.set('city', activeCity.value);
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
      },
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () => {
        const items = servicesList.value.slice(0, 10).map((service, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Service',
            name: service.title,
            provider: {
              '@type': 'LocalBusiness',
              name: service.partner.businessName,
            },
            ...(service.location?.city && {
              areaServed: { '@type': 'City', name: service.location.city },
            }),
            ...(service.price != null && {
              offers: {
                '@type': 'Offer',
                price: (service.price / 100).toFixed(2),
                priceCurrency: service.currency || 'EUR',
              },
            }),
            url: service.slug && service.partner?.slug ? `${siteUrl}/p/${service.partner.slug}/s/${service.slug}` : undefined,
          },
        }));
        return JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: $ts('servicesSearch.title'),
          numberOfItems: servicesList.value.length,
          itemListElement: items,
        });
      },
    },
  ],
});

// Update URL with current filters
function updateUrl() {
  const query: Record<string, string> = {};
  if (activeSearchQuery.value) query.q = activeSearchQuery.value;
  if (activeServiceType.value) query.type = activeServiceType.value;
  if (activeCity.value) query.city = activeCity.value;
  if (activeSort.value && activeSort.value !== 'popular') query.sort = activeSort.value;
  router.replace({ query });
}

// Search button — applies text inputs to active filters
function handleSearch() {
  activeSearchQuery.value = searchQuery.value || undefined;
  activeCity.value = locationQuery.value || undefined;
  updateUrl();
}

// Hero picker handlers — wrap existing handlers so URL sync + PostHog continue to fire
function onHeroServiceSelect(service: ServiceType) {
  handleServiceTypeChange(service.key);
  captureEvent('hero_service_selected', { service: service.key });
}

function onHeroServiceClear() {
  handleServiceTypeChange(undefined);
}

function onHeroCitySelect(result: GeocodeResult) {
  const cityName = result.placeName ?? locationQuery.value;
  handleCityChange(cityName || undefined);
  if (result.center) {
    userLat.value = result.center[1];
    userLng.value = result.center[0];
  }
  captureEvent('hero_city_selected', { city: cityName });
}

function handleServiceTypeChange(value: string | undefined) {
  activeServiceType.value = value;
  updateUrl();
}

function handleCityChange(value: string | undefined) {
  activeCity.value = value;
  locationQuery.value = value || '';
  // Resolve city center coordinates for distance-based search
  if (value && CITY_CENTERS[value]) {
    userLat.value = CITY_CENTERS[value].lat;
    userLng.value = CITY_CENTERS[value].lng;
  } else if (!value) {
    // Only clear coords if they came from city selection (not GPS)
    userLat.value = undefined;
    userLng.value = undefined;
  }
  updateUrl();
}

function handleRadiusChange(value: number) {
  searchRadius.value = value;
}

function handleRequestLocation() {
  if (!import.meta.client || !navigator.geolocation) return;
  isLocating.value = true;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      userLat.value = position.coords.latitude;
      userLng.value = position.coords.longitude;
      isLocating.value = false;
      captureEvent('search_location_detected', {
        source: 'browser_gps',
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    },
    () => {
      isLocating.value = false;
      captureEvent('search_location_denied', { reason: 'browser_denied' });
    },
    { enableHighAccuracy: false, timeout: 10000 },
  );
}

function handleSortChange(value: string) {
  activeSort.value = value;
  updateUrl();
}

function clearFilters() {
  activeServiceType.value = undefined;
  activeCity.value = undefined;
  activeSearchQuery.value = undefined;
  searchQuery.value = '';
  locationQuery.value = '';
  activeSort.value = 'popular';
  activeMinRating.value = undefined;
  activeMinPrice.value = undefined;
  activeMaxPrice.value = undefined;
  userLat.value = undefined;
  userLng.value = undefined;
  updateUrl();
}
</script>
